import {
    useEffect,
    useRef,
    useState,
} from 'react';
import { TextInputHelperCurrency } from '@honeycomb-npm/honeycomb-react';
import { TextField } from '@honeycomb-npm/honeycomb-react';

import { getSoftValidationMessage, getValidationMessage } from '../../../utils/contentUtil';
import {
    GenericBladeParsedDataType,
    GenericFormAEM,
    NodePayload,
    ValidationInfo,
} from '../genericBladeTypes';
import GenericBladeFormHeading from './BladeFormHeading';

type PropsT = {
    id: string;
    genericFormAEM: Record<string, GenericFormAEM>;
    genericFormApiData?: GenericBladeParsedDataType;
    validations?: ValidationInfo;
    handleChange: (node: string, value: NodePayload) => void;
    errors: Record<string, string>;
};

export const BladeInput = ({
    id,
    genericFormAEM,
    genericFormApiData,
    validations,
    handleChange,
    errors,
}: PropsT) => {
    const [inputValue, setInputValue] = useState<Record<string, NodePayload>>(
        Object.entries(genericFormApiData ?? {}).reduce((result, [key, data]) => (
            (data.value || data.value === 0) ? { ...result, [key]: data.value } : result
        ), {} as Record<string, NodePayload>),
    );

    const [softValidationMessage, setSoftValidationMessage] = useState<
        Record<string, string>
    >(
        Object.keys(genericFormAEM).reduce((result, key) => {
            const validationMessage = getSoftValidationMessage(
                validations,
                key,
                (inputValue?.[key] ?? '') as string,
            );

            return {
                ...result,
                [key]: validationMessage,
            };
        }, {} as Record<string, string>),
    );

    const inputRef = useRef<Record<string, HTMLInputElement | null>>(
        Object.keys(genericFormAEM).reduce((result, key) => ({
            ...result,
            [key]: null,
        }), {} as Record<string, null>),
    );

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            Object.keys(genericFormAEM).forEach((key) => {
                inputRef.current?.[key]?.focus();
                inputRef.current?.[key]?.blur();
            });

            for (const key of Object.keys(genericFormAEM)) {
                if (errors[key]) {
                    setTimeout(() => {
                        inputRef.current?.[key]?.focus();
                    }, 0);
                    break;
                }
            }
        }
    }, [errors]);

    const handleEditInputValue = (node: string, newValue: NodePayload) => {
        setInputValue((value) => ({ ...value, [node]: newValue }));
        handleChange(node, newValue);
        const validationMessage = getSoftValidationMessage(
            validations,
            node,
            newValue as string,
        );

        setSoftValidationMessage((message) => ({
            ...message,
            [node]: validationMessage,
        }));
    };

    const convertToNumber = (value: string | undefined) => value ? parseFloat(value) : undefined;

    const getMessage = (node: string) => {
        if (!validations?.hardValidations?.[node]) {
            return '';
        }

        if (inputValue?.[node] === '') {
            return validations?.hardValidations?.[node]?.minValueMessage;
        }

        return getValidationMessage(validations?.hardValidations?.[node], inputValue?.[node]);
    };

    const getHelpTextContent = (node:string) => {
        if((!errors[node] || inputValue?.[node]) && softValidationMessage?.[node]) {
            return softValidationMessage?.[node];
        }

        return undefined;
    };

    return (
        <>
            {Object.entries(genericFormAEM).map(([node, content]) => (
                <div key={node}>
                    <GenericBladeFormHeading
                        {...{
                            id,
                            node,
                            heading: content.heading,
                            description: content.description,
                        }}
                    ></GenericBladeFormHeading>
                    {content.fieldType === 'currency' ? (
                        <div className="p-0 col-12 col-md-5 mb-4">
                            <TextInputHelperCurrency
                                id={`generic-blade-input-${id}-${node}`}
                                label={content.label ?? 'label'}
                                onChange={(_: string, value: NodePayload) =>
                                    handleEditInputValue(node, value)
                                }
                                placeholder={content.defaultValue ?? '$'}
                                value={(inputValue?.[node] ?? '') as string}
                                required
                                min={convertToNumber(validations?.hardValidations?.[node].minValue)}
                                errormessageMin={getMessage(node)}
                                errormessage={errors?.[node]}
                                max={convertToNumber(validations?.hardValidations?.[node]?.maxValue)}
                                errormessageMax={validations?.hardValidations?.[node]?.maxValueMessage}
                                isValid={!errors[node]}
                                forwardedRef={(el: HTMLInputElement) =>
                                    (inputRef.current[node] = el)
                                }
                                autoComplete='off'
                                inputMode='decimal'
                                helptext={getHelpTextContent(node)}
                            />
                        </div>
                    ) : (
                        <div className="p-0 col-12 col-md-5 mb-4">
                            <TextField
                                id={`generic-blade-input-${id}-${node}`}
                                name={`generic-blade-input-${id}-${node}`}
                                onChange={(_: string, value: NodePayload) =>
                                    handleEditInputValue(node, value)
                                }
                                value={inputValue?.[node] ?? ''}
                                label={content.label}
                                errormessage={errors[node]}
                                isValid={!errors[node]}
                                helptext={getHelpTextContent(node)}
                            />
                        </div>
                    )}
                    {content.showFormDivider && (
                        <hr aria-hidden="true" className="mb-4" />
                    )}
                </div>
            ))}
        </>
    );
};
